// Pure, framework-free auth-session logic for the shared Rithmo sign-in work (Stage 3). Extracted from AuthContext so the
// security-relevant decisions (role resolution, validate-on-restore, SSO completion) are unit-testable in a node
// environment — this repo has no jsdom/testing-library, and the SPA cannot add one (node_modules is a shared symlink).

/**
 * SERVER-AUTHORITATIVE role. The backend sends the user's role (or none, in which case we default to 'member'). We NEVER
 * promote to admin from a name/email heuristic. Historically the SPA promoted anyone whose name/email merely *contained*
 * "tim" to admin — a client-side guess that could mislabel unrelated users; it is removed here. Platform-admin access is
 * gated separately by a server-matched email allowlist (utils/platformAdmin.js + server requirePlatformAdmin.ts).
 */
export function resolveRole(userData) {
  const role = userData?.role
  if (role === 'admin') return 'admin'
  if (role === 'member') return 'member'
  if (role === 'user') return 'member' // legacy vocabulary → member
  return 'member'
}

/**
 * Decide how to restore a session on app load from the result of the server validation call (GET /api/auth/validate).
 * The SPA trusts the SERVER, not localStorage — but must NOT regress when validation is unavailable (the SSO feature flag
 * is off → the endpoint 404s) or when the network is down.
 *
 *  - server returned a result  → adopt the server identity + active org (authoritative).
 *  - 401                       → the session is gone server-side (e.g. an SSO session hit its finite lifetime, O1) → sign out.
 *  - 404 / network / 5xx       → validation unavailable → keep the existing local session (flag-off behaves exactly as before).
 *
 * @param {Object}      args
 * @param {Object|null} args.localUser    user parsed from localStorage (may be null)
 * @param {Object|null} args.serverResult on success: { user?, org? } from /validate; null when the call failed
 * @param {number|null} args.errorStatus  HTTP status when the call failed (401 = session gone; 404 = flag off); null on success
 * @returns {{ action: 'adopt-server'|'keep-local'|'sign-out', user: Object|null }}
 */
export function interpretValidation({ localUser = null, serverResult = null, errorStatus = null } = {}) {
  if (serverResult) {
    const serverUser = serverResult.user ?? null
    const serverOrg = serverResult.org ?? null
    const merged = { ...(localUser || {}) }
    if (serverUser) {
      if (serverUser.id != null) merged.id = serverUser.id
      if (serverUser.email) merged.email = serverUser.email
      // Missing name → fall back to email for display (no profile form is ever shown — plan §Stage 3).
      merged.name = serverUser.full_name || serverUser.name || serverUser.email || merged.name
    }
    if (serverOrg) merged.activeOrg = serverOrg
    // Role is SERVER-authoritative: resolve from the server identity, not the (possibly stale/tampered) localStorage role.
    // The server currently sends no platform role on /validate, so this yields 'member'; real admin access is the email
    // allowlist. This is the whole point of validate-on-restore — localStorage can never inflate the role.
    merged.role = resolveRole(serverUser ?? merged)
    return { action: 'adopt-server', user: merged }
  }
  if (errorStatus === 401) {
    return { action: 'sign-out', user: null }
  }
  if (localUser) {
    return { action: 'keep-local', user: { ...localUser, role: resolveRole(localUser) } }
  }
  return { action: 'sign-out', user: null }
}

/**
 * Guard against a STALE auth response acting on a NEWER session. A request captures the token it was issued against; by the
 * time its (possibly delayed) response lands, a newer login — e.g. an SSO completion — may have replaced the stored token.
 * Returns true only when the response may still act (the stored token is unchanged), so:
 *   - a late /validate success for an old token must NOT replace the new identity,
 *   - a late 401 for an old token must NOT clear the new session.
 * A falsy expectedToken means "origin unknown" → treat as current (preserve legacy behavior; never block a real clear).
 */
export function isSessionCurrent(expectedToken, currentToken) {
  if (!expectedToken) return true
  return expectedToken === currentToken
}

/**
 * Map an SSO completion response (POST /api/auth/sso/complete) to a stored Rithmo session. The bearer is returned in the
 * response BODY only (never a cookie, never a URL) and is stored exactly like a password login. Throws if no token.
 *
 * @param {Object} payload { token, user?: { id, email, full_name }, org?, return_to? }
 * @returns {{ token: string, user: Object, returnTo: string }}
 */
export function buildSsoSession(payload) {
  const token = payload?.token
  if (!token || typeof token !== 'string') {
    throw new Error('SSO completion returned no session token')
  }
  const serverUser = payload.user ?? null
  const serverOrg = payload.org ?? null
  const user = {
    id: serverUser?.id,
    email: serverUser?.email,
    name: serverUser?.full_name || serverUser?.name || serverUser?.email,
    emailVerified: true,
    activeOrg: serverOrg || null,
    role: 'member',
    settings: { notifications: true, theme: 'dark', defaultMeetingDuration: 30 },
    integrations: { google: false, outlook: false, zoom: false },
  }
  user.role = resolveRole(user)
  const returnTo = typeof payload.return_to === 'string' && payload.return_to ? payload.return_to : '/dashboard'
  return { token, user, returnTo }
}
